import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { DRIFT_ALERTS_QUERY_KEY } from "./constants";
import type { DriftAlert } from "./types";

interface ResolveDriftAlertParams {
  readonly id: string;
  /** Optional operator note; blank notes are omitted from the request. */
  readonly note?: string;
}

/**
 * Hand-written fetch — see CLAUDE.md → TD-07. `POST .../:id/resolve`
 * is a CAS on `resolved_at IS NULL`; a 404 means another operator
 * already resolved it (or the id is unknown).
 */
async function resolveDriftAlert({ id, note }: ResolveDriftAlertParams): Promise<DriftAlert> {
  const trimmed = note?.trim();
  return httpClient<DriftAlert>(
    `/api/admin/subscriptions/drift-alerts/${encodeURIComponent(id)}/resolve`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(trimmed ? { note: trimmed } : {}),
    },
  );
}

export function useResolveDriftAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resolveDriftAlert,
    onSuccess: () => {
      // The resolved row drops off the open-alerts list — refetch so
      // the queue and its total count stay accurate.
      queryClient.invalidateQueries({ queryKey: DRIFT_ALERTS_QUERY_KEY });
    },
  });
}
